import {
  Avatar, Box, Card, CardContent, Chip, Grid, Stack, Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import EventIcon from '@mui/icons-material/Event';
import VideocamIcon from '@mui/icons-material/Videocam';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import ChatIcon from '@mui/icons-material/Chat';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { PageHeader } from '@/components/PageHeader';
import { CenteredLoader } from '@/components/CenteredLoader';
import { EmptyState } from '@/components/EmptyState';
import { TriageBadge } from '@/components/SeverityChip';
import { useApiQuery } from '@/hooks/useApiQuery';
import { clinician } from '@/api/endpoints';
import { formatTime, formatDateTime } from '@/utils/format';
import type { AppointmentListItem, Modality, TriageLevel } from '@/types/medical';

/**
 * Clinician "Today".
 *
 * The schedule for today (in clinician's tz) is the headline. We also show:
 *   - a "next up" hero with the very next appointment,
 *   - a small panel of yesterday-or-earlier appointments still without a
 *     locked encounter (clinician-side reminder).
 *
 * AI suggestions awaiting decision are listed in a dedicated section that
 * the AI log page also exposes — Tour 3 builds the same component once.
 */

export function ClinicianTodayPage() {
  const profileQ = useApiQuery({
    queryKey: ['clinician', 'me'],
    queryFn:  clinician.me.get,
  });
  const apptsQ = useApiQuery({
    queryKey: ['clinician', 'appointments', 'all'],
    queryFn:  () => clinician.appointments.list(),
  });

  const buckets = useMemo(() => splitByDay(apptsQ.data ?? []), [apptsQ.data]);

  if (profileQ.isPending && apptsQ.isPending) {
    return <CenteredLoader label="Loading your day…" />;
  }

  return (
    <>
      <PageHeader
        title={`Hi, ${profileQ.data?.display_name?.split(' ')[0] || 'doc'}.`}
        subtitle="Your day at a glance."
      />

      {buckets.next ? (
        <NextUpHero appointment={buckets.next} />
      ) : (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <EmptyState
              icon={<EventIcon />}
              title="Nothing on right now"
              description="Your next appointment will appear here as soon as it's coming up."
            />
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <SectionHeader label="Today" count={buckets.today.length} />
          {buckets.today.length === 0 ? (
            <EmptyState title="No more appointments today" />
          ) : (
            <Stack spacing={1.5}>
              {buckets.today.map((a) => <Row key={a.id} appointment={a} />)}
            </Stack>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <SectionHeader label="Tomorrow" count={buckets.tomorrow.length} />
          {buckets.tomorrow.length === 0 ? (
            <EmptyState title="Empty so far" />
          ) : (
            <Stack spacing={1.5}>
              {buckets.tomorrow.slice(0, 5).map((a) => (
                <Row key={a.id} appointment={a} compact />
              ))}
            </Stack>
          )}

          {buckets.openOlder.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <SectionHeader label="Needs your attention"
                             count={buckets.openOlder.length}
                             helper="Past appointments still without a locked encounter." />
              <Stack spacing={1.5}>
                {buckets.openOlder.slice(0, 5).map((a) => (
                  <Row key={a.id} appointment={a} compact dimDate={false} attention />
                ))}
              </Stack>
            </Box>
          )}
        </Grid>
      </Grid>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function SectionHeader({
  label, count, helper,
}: { label: string; count: number; helper?: string }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography variant="overline" color="text.secondary">{label}</Typography>
        <Typography variant="overline" color="text.secondary" className="tabular">
          ({count})
        </Typography>
      </Stack>
      {helper && (
        <Typography variant="caption" color="text.secondary">{helper}</Typography>
      )}
    </Box>
  );
}

function NextUpHero({ appointment }: { appointment: AppointmentListItem }) {
  return (
    <Card
      component={RouterLink}
      to={`/clinician/appointments/${appointment.id}`}
      sx={{
        mb: 4, textDecoration: 'none', color: 'inherit',
        borderColor: 'primary.main', borderWidth: 2, borderStyle: 'solid',
        '&:hover': { boxShadow: 4 },
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={3} alignItems="center">
          <ModalityIcon modality={appointment.modality} large />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="overline" color="primary.main">
              Next up
            </Typography>
            <Typography variant="h5" component="h2" sx={{ mb: 0.5 }} noWrap>
              {appointment.procedure_name}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" color="text.secondary">
                Patient {appointment.patient_initials}
              </Typography>
              <Typography variant="body2" color="text.secondary" className="tabular">
                {formatTime(appointment.start_at)} – {formatTime(appointment.end_at)}
              </Typography>
              {appointment.ai_triage_level && (
                <TriageBadge level={appointment.ai_triage_level as TriageLevel} small />
              )}
            </Stack>
          </Box>
          <ChevronRightIcon color="primary" />
        </Stack>
      </CardContent>
    </Card>
  );
}

function Row({
  appointment, compact, dimDate = true, attention = false,
}: {
  appointment: AppointmentListItem;
  compact?: boolean;
  dimDate?: boolean;
  attention?: boolean;
}) {
  return (
    <Card
      component={RouterLink}
      to={`/clinician/appointments/${appointment.id}`}
      sx={{
        textDecoration: 'none', color: 'inherit',
        borderColor: attention ? 'warning.main' : 'divider',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <CardContent sx={{ py: compact ? 1.25 : 2, '&:last-child': { pb: compact ? 1.25 : 2 } }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: 'background.subtle', color: 'primary.main', width: 36, height: 36 }}>
            {appointment.patient_initials.replace(/\./g, '')}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
              <Typography variant="body1" fontWeight={600} noWrap>
                {appointment.procedure_name}
              </Typography>
              {appointment.ai_triage_level && (
                <TriageBadge level={appointment.ai_triage_level as TriageLevel} small />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" noWrap>
              {appointment.patient_initials} ·{' '}
              <span className="tabular">
                {dimDate ? formatTime(appointment.start_at) : formatDateTime(appointment.start_at)}
              </span>
            </Typography>
          </Box>
          <ModalityIcon modality={appointment.modality} />
          <Chip
            size="small"
            label={appointment.status.replace(/_/g, ' ')}
            variant="outlined"
            sx={{ textTransform: 'capitalize' }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

function ModalityIcon({ modality, large }: { modality: Modality; large?: boolean }) {
  const size = large ? 56 : 32;
  const icon = {
    in_person: <LocationOnIcon />,
    video:     <VideocamIcon />,
    phone:     <PhoneIcon />,
    async:     <ChatIcon />,
    home:      <HomeWorkIcon />,
    hybrid:    <EventIcon />,
  }[modality];
  return (
    <Box sx={{
      width: size, height: size, flexShrink: 0,
      borderRadius: 1.5,
      bgcolor: 'background.subtle', color: 'primary.main',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {icon}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function splitByDay(all: AppointmentListItem[]) {
  const now = new Date();
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const startOfTomorrow = new Date(startOfToday);
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
  const startOfDayAfter = new Date(startOfTomorrow);
  startOfDayAfter.setDate(startOfDayAfter.getDate() + 1);

  const today: AppointmentListItem[] = [];
  const tomorrow: AppointmentListItem[] = [];
  const openOlder: AppointmentListItem[] = [];
  let next: AppointmentListItem | null = null;

  for (const a of all) {
    const start = new Date(a.start_at);
    if (a.status === 'completed' || a.status === 'cancelled_pt' ||
        a.status === 'cancelled_dr' || a.status === 'no_show' ||
        a.status === 'refunded') {
      continue;
    }
    if (start >= startOfToday && start < startOfTomorrow) {
      today.push(a);
      if (start >= now && (!next || new Date(next.start_at) > start)) {
        next = a;
      }
    } else if (start >= startOfTomorrow && start < startOfDayAfter) {
      tomorrow.push(a);
    } else if (start < startOfToday) {
      // appointments in the past that the clinician hasn't completed yet
      openOlder.push(a);
    }
  }

  today.sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
  tomorrow.sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
  openOlder.sort((a, b) => new Date(b.start_at).getTime() - new Date(a.start_at).getTime());

  return { today, tomorrow, openOlder, next };
}
